import type { ChannelMetricsItem, SubchannelMetricsItem } from '@/app/api/connectors/catalog/metrics/route'
import type { ChannelView } from '@/components/budget/BudgetAllocation'
import { DEMO_WALLET } from '@/lib/fixtures/demo-brand'
import { FALLBACK_MARKETPLACE_CHANNELS } from '@/lib/marketplace/fallback-data'
import { FALLBACK_OVERVIEW } from '@/lib/overview/fallback-data'

function findSubchannelKey(channelKey: string, subName: string): string | null {
  const channel = FALLBACK_MARKETPLACE_CHANNELS.find((c) => c.key === channelKey)
  const sub = channel?.subchannels.find((s) => s.name === subName)
  return sub?.key ?? null
}

/** Connected subchannels with monthly budgets for logged-out budget preview. */
export function getBudgetExampleState(): {
  channels: ChannelView[]
  channelMetrics: Record<string, ChannelMetricsItem>
  subchannelMetrics: Record<string, SubchannelMetricsItem>
  walletCents: number
  monthlyCapCents: number
  smartSpendEnabled: boolean
} {
  const overviewByKey = new Map(FALLBACK_OVERVIEW.map((ch) => [ch.key, ch]))

  const channels: ChannelView[] = FALLBACK_MARKETPLACE_CHANNELS.map((channel) => {
    const overview = overviewByKey.get(channel.key as (typeof FALLBACK_OVERVIEW)[number]['key'])
    const subSpend = overview?.subs ?? []

    return {
      key: channel.key,
      name: channel.name,
      accent_hex: channel.accent_hex ?? '#0A8A6E',
      display_order: channel.display_order ?? 0,
      description: channel.description ?? null,
      subchannels: subSpend.map((sub) => {
        const catalogSub = channel.subchannels.find((s) => s.name === sub.name)
        return {
          key: catalogSub?.key ?? `${channel.key}-${sub.name.toLowerCase().replace(/\s+/g, '-')}`,
          channel_key: channel.key,
          name: sub.name,
          status: 'available' as const,
          connection: {
            id: `example-${catalogSub?.key ?? sub.name}`,
            auth_mode: 'atrium_managed' as const,
            status: 'connected' as const,
            monthly_budget_cents: Math.round(sub.spend * 100),
          },
        }
      }),
    }
  })

  const channelMetrics: Record<string, ChannelMetricsItem> = {}
  const subchannelMetrics: Record<string, SubchannelMetricsItem> = {}

  for (const ch of FALLBACK_OVERVIEW) {
    if (ch.spend <= 0) continue
    const impressions = Math.round(ch.spend * 120)
    const clicks = Math.round(impressions * 0.02)
    channelMetrics[ch.key] = {
      channel_key: ch.key,
      active_campaign_count: ch.live,
      spend_cents: ch.spend * 100,
      impressions,
      clicks,
      conversions: Math.round(clicks * 0.08),
      purchase_revenue_cents: Math.round(ch.spend * ch.roas * 100),
      ctr: clicks / impressions,
      roas: ch.roas,
    }
    for (const sub of ch.subs) {
      const subKey = findSubchannelKey(ch.key, sub.name)
      if (!subKey) continue
      const subImpressions = Math.round(sub.spend * 120)
      const subClicks = Math.round(subImpressions * 0.02)
      subchannelMetrics[subKey] = {
        subchannel_key: subKey,
        channel_key: ch.key,
        active_campaign_count: 0,
        spend_cents: sub.spend * 100,
        impressions: subImpressions,
        clicks: subClicks,
        conversions: Math.round(subClicks * 0.08),
        purchase_revenue_cents: Math.round(sub.spend * sub.roas * 100),
        ctr: subClicks / subImpressions,
        roas: sub.roas,
      }
    }
  }

  const totalSpend = FALLBACK_OVERVIEW.reduce((sum, ch) => sum + ch.spend, 0)

  return {
    channels,
    channelMetrics,
    subchannelMetrics,
    walletCents: DEMO_WALLET.balanceCents,
    monthlyCapCents: totalSpend * 100,
    smartSpendEnabled: true,
  }
}
